import { useEffect, useState } from 'react';
import { APP_VERSION } from '../constants/appRelease';

interface VersionCheckerOptions {
  checkInterval?: number; // in milliseconds
  enabled?: boolean;
  onUpdateDetected?: () => void;
  autoReload?: boolean;
  reloadDelay?: number; // delay before auto-reload in milliseconds
}

export const useVersionChecker = (options: VersionCheckerOptions = {}) => {
  const {
    checkInterval = 5 * 60 * 1000, // Check every 5 minutes by default
    enabled = true,
    onUpdateDetected,
    autoReload = true,
    reloadDelay = 3000, // 3 seconds delay before reload
  } = options;

  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const checkForUpdate = async () => {
      try {
        // Fetch the current index.html with a cache-busting query parameter
        const response = await fetch(`/index.html?t=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        });

        if (!response.ok) {
          console.warn('Failed to check for updates');
          return;
        }

        const html = await response.text();

        // Check if the HTML content has changed by comparing build file references
        const buildFileMatch = html.match(/static\/js\/main\.[a-f0-9]+\.js/);
        const currentBuildFile = document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src');

        console.log('Version check running...');
        console.log('Deployed build file:', buildFileMatch ? buildFileMatch[0] : 'Not found');
        console.log('Current build file:', currentBuildFile || 'Not found');

        if (buildFileMatch && currentBuildFile) {
          const deployedBuildFile = buildFileMatch[0];
          const currentBuildFileName = currentBuildFile.split('/').pop();
          const dismissedVersion = localStorage.getItem('dismissedVersion');

          if (deployedBuildFile !== `static/js/${currentBuildFileName}`) {
            // Check if user already dismissed this specific version
            if (dismissedVersion === deployedBuildFile) {
              console.log('ℹ️ Version already dismissed by user:', deployedBuildFile);
              return;
            }

            console.log('✅ New build detected (build file changed)');
            console.log('Deployed:', deployedBuildFile);
            console.log('Current:', currentBuildFileName);

            setLatestVersion(deployedBuildFile);
            setNewVersionAvailable(true);

            if (onUpdateDetected) {
              onUpdateDetected();
            }

            if (autoReload) {
              console.log(`Auto-reloading in ${reloadDelay}ms...`);
              setTimeout(() => {
                window.location.reload(); // Hard reload
              }, reloadDelay);
            }
          } else {
            console.log('✓ Build files match - no update needed');
            // Clear dismissed version when files match (user successfully updated)
            if (dismissedVersion) {
              console.log('🧹 Clearing dismissed version flag');
              localStorage.removeItem('dismissedVersion');
            }
          }
        } else {
          console.warn('⚠️ Could not detect build files for version checking');
          console.log('This might be development mode or build files not found');
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    };

    // Check immediately on mount
    checkForUpdate();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkForUpdate, checkInterval);

    // Check when user returns to the tab (after being away)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Tab became visible, checking for updates...');
        checkForUpdate();
      }
    };

    // Check when window regains focus
    const handleFocus = () => {
      console.log('Window gained focus, checking for updates...');
      checkForUpdate();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup interval and event listeners on unmount
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, checkInterval, onUpdateDetected, autoReload, reloadDelay]);

  const forceReload = () => {
    console.log('🔄 Force reload triggered...');

    // Store the version we're reloading for to prevent re-showing notification
    if (latestVersion) {
      localStorage.setItem('dismissedVersion', latestVersion);
      console.log('💾 Stored dismissed version:', latestVersion);
    }

    // Unregister all service workers and clear cache before reloading
    if ('serviceWorker' in navigator) {
      console.log('🧹 Clearing service worker cache...');
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        const unregisterPromises = registrations.map(registration => {
          console.log('Unregistering service worker:', registration.scope);
          return registration.unregister();
        });

        // Wait for all service workers to unregister, then reload
        Promise.all(unregisterPromises).then(() => {
          console.log('✅ All service workers unregistered, reloading...');
          // Hard reload to bypass cache
          window.location.reload();
        }).catch(err => {
          console.error('Error unregistering service workers:', err);
          // Reload anyway
          window.location.reload();
        });
      }).catch(err => {
        console.error('Error getting service worker registrations:', err);
        // Reload anyway
        window.location.reload();
      });
    } else {
      // No service worker support, just reload
      window.location.reload();
    }
  };

  return {
    newVersionAvailable,
    latestVersion,
    currentVersion: APP_VERSION,
    forceReload,
  };
};
